import { ErrorBoundary, Shadow, Text, VStack } from '@genial/components';
import { BoxBorder, BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box, Center, FlatList } from 'native-base';
import React from 'react';

import { Skeleton } from '../../Skeleton';
import { MarginRequiredItem } from '../MarginRequiredItem/intex';
import { PositionWarrantyItem } from '../PositionWarrantyItem/intex';
import { TableHead } from '../TableHead';
import {
  marginRequiredWidthCols,
  positionWarrantyWidthCols,
  tableHeadMarginRequired,
  tableHeadPositionWarranty,
} from '../TableHead/constants';

interface ListProps {}

const ItemSeparatorComponent = () => (
  <Box w="full" h="1px" my={Spacing['stack.xxxs']} bgColor={Color['neutral.lightest']} />
);

export const List: React.FC<ListProps> = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const viewportType = useBreakpointValue({ sm: 'tablet', lg: 'desktop' });

  const error = false;
  const loading = false;
  const data = [1];

  if (loading) {
    return (
      <Shadow
        borderRadius={BoxBorder.heavy}
        mx={{ base: Spacing['inline.xxxs'], md: Spacing['inline.xxxs'] }}
        mb={Spacing['inline.xxs']}
        level={3}>
        <Skeleton viewportType={viewportType} renderCount={8} />
      </Shadow>
    );
  }

  if (error) {
    return (
      <Center h={'400px'}>
        <ErrorBoundary onRefresh={() => {}} />
      </Center>
    );
  }

  if (!loading && data && data.length === 0) {
    return (
      <Center h={'400px'}>
        <ErrorBoundary
          errorIconOrIllustration="NotFoundIllustration"
          errorTitle="Nenhum resultado corresponde à sua pesquisa"
          errorMessage="Por favor, verifique o campo informado e tente novamente."
        />
      </Center>
    );
  }

  return (
    <Shadow
      level={3}
      mb={Spacing['inline.xxs']}
      borderRadius={BoxBorder.heavy}
      mx={{ base: Spacing['inline.xxxs'], md: Spacing['inline.xxxs'] }}>
      <VStack
        px={Spacing['inline.xxxs']}
        py={Spacing['inline.xxs']}
        alignItems="center"
        space={Spacing['inline.xxxs']}
        justifyContent="space-between"
        borderRadius={BoxBorder.heavy}>
        <Center bg="background" borderRadius={BoxRadius.sm} w="full" py={Spacing['inset.nano']}>
          <Text color="neutral.dark" variant="bold" fontSize={Typography['fontsize.lg']}>
            Margem requerida
          </Text>
        </Center>
        <VStack w="full" space={Spacing['inline.xxxs']}>
          <TableHead tableHead={tableHeadMarginRequired} widthCols={marginRequiredWidthCols} />
          <FlatList
            data={[1, 2, 3, 4]}
            showsVerticalScrollIndicator={false}
            keyExtractor={(_, index) => `margin-required-${index}`}
            {...(isMobile && { ItemSeparatorComponent })}
            renderItem={() => {
              return <MarginRequiredItem />;
            }}
          />
        </VStack>
        <Center bg="background" borderRadius={BoxRadius.sm} w="full" py={Spacing['inset.nano']}>
          <Text color="neutral.dark" variant="bold" fontSize={Typography['fontsize.lg']}>
            Posição em garantia
          </Text>
        </Center>
        <VStack w="full" space={Spacing['inline.xxxs']}>
          <TableHead tableHead={tableHeadPositionWarranty} widthCols={positionWarrantyWidthCols} />
          <FlatList
            data={[1, 2, 3, 4]}
            showsVerticalScrollIndicator={false}
            {...(isMobile && { ItemSeparatorComponent })}
            keyExtractor={(_, index) => `position-warranty-${index}`}
            renderItem={() => {
              return <PositionWarrantyItem />;
            }}
          />
        </VStack>
      </VStack>
    </Shadow>
  );
};
